import { useSyncExternalStore } from 'react';
import { apiBaseUrl } from './portal';

/**
 * Deployment runtime config (modelled on GIS-EYE/src/runtimeConfig.js).
 *
 * A hosted deployment can switch features off per environment (backend
 * features.py). The client learns which from the public `GET /api/config`,
 * fetched once at boot, and hides the matching UI instead of letting users hit
 * a 403. ANY failure to fetch resolves to "everything enabled": a backend blip
 * or an older backend without the endpoint must never hide UI.
 */

const API_BASE_URL = apiBaseUrl();

const FALLBACK = Object.freeze({ ok: false, version: null, features: Object.freeze({}) });

let _promise = null;
let _config = FALLBACK; // resolved value, readable synchronously once loaded
let _loaded = false; // true once the fetch has settled (success or fallback)
const _listeners = new Set();

function _publish(config, loaded = true) {
  _config = config;
  _loaded = loaded;
  _listeners.forEach((fn) => fn());
}

/** Fetch (once) the deployment config; resolves the fallback on any failure. */
export function loadRuntimeConfig({ fetchImpl } = {}) {
  if (!_promise) {
    const doFetch = fetchImpl || ((...args) => fetch(...args));
    _promise = (async () => {
      try {
        const response = await doFetch(`${API_BASE_URL}/api/config`, { cache: 'no-store' });
        if (!response.ok) return FALLBACK;
        const parsed = await response.json();
        if (!parsed || typeof parsed !== 'object' || typeof parsed.features !== 'object' || !parsed.features) {
          return FALLBACK;
        }
        return {
          ok: true,
          version: typeof parsed.version === 'string' ? parsed.version : null,
          features: parsed.features,
        };
      } catch {
        return FALLBACK;
      }
    })().then((config) => {
      _publish(config);
      return config;
    });
  }
  return _promise;
}

/** Reset the cached fetch (tests only). */
export function resetRuntimeConfigForTests() {
  _promise = null;
  _publish(FALLBACK, false);
}

/** True unless the backend explicitly reported the feature as disabled. */
export function isFeatureEnabled(key) {
  return _config.features[key] !== false;
}

function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/** React hook: re-renders when the config finishes loading. */
export function useFeature(key) {
  return useSyncExternalStore(subscribe, () => isFeatureEnabled(key));
}

/** React hook: false until /api/config has settled (success or fallback). */
export function useRuntimeConfigLoaded() {
  return useSyncExternalStore(subscribe, () => _loaded);
}
